/**
 * Resources Panel — HMR-aware shell
 *
 * Registers a side pane that shows all packed resources in the current scene's
 * ResourceManager. Follows the same HMR pattern as catalog-panel.tsx so edits
 * to resources-panel-impl.tsx hot-reload without a full page refresh.
 */

import React, { useState, useEffect, useRef } from 'react';
import type {
    ServiceDefinition,
    IShellService,
    SidePaneDefinition
} from '@babylonjs/inspector';
import { ShellServiceIdentity } from '@babylonjs/inspector';
import { ResourcesPanelImpl } from './resources-panel-impl';

// ============================================================================
// HMR Wrapper
// ============================================================================

const ResourcesPanel: React.FC = () => {
    const [Impl, setImpl] = useState<React.FC>(() => ResourcesPanelImpl);
    const counterRef = useRef(0);

    useEffect(() => {
        if (import.meta.hot) {
            import.meta.hot.accept('./resources-panel-impl', (mod) => {
                const Comp = mod?.ResourcesPanelImpl as React.FC | undefined;
                if (Comp) {
                    counterRef.current++;
                    setImpl(() => Comp);
                }
            });
        }
    }, []);

    return <Impl key={counterRef.current} />;
};

// ============================================================================
// Service Definition
// ============================================================================

const ResourcesIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8.186 1.113a.5.5 0 00-.372 0L1.846 3.5 8 5.961 14.154 3.5 8.186 1.113zM15 4.239l-6.5 2.488v7.674l6.5-2.488V4.239zM7.5 14.401V6.727L1 4.239v7.674l6.5 2.488z"/>
    </svg>
);

export const ResourcesPanelServiceDefinition: ServiceDefinition<[], [IShellService]> = {
    friendlyName: 'Resources Panel',

    consumes: [ShellServiceIdentity],

    factory: (shellService: IShellService) => {
        const paneDefinition: SidePaneDefinition = {
            key: 'resources-panel',
            icon: ResourcesIcon,
            content: ResourcesPanel,
            horizontalLocation: 'right',
            verticalLocation: 'top',
            order: 4,
            title: 'Resources'
        };

        const disposable = shellService.addSidePane(paneDefinition);

        return {
            dispose: () => {
                disposable.dispose();
            }
        };
    }
};
